import { z } from "zod";
import { CreateWorkItemSchema, WorkDepartmentSchema, WorkKindSchema, WorkLocationReferenceSchema, WorkPrioritySchema, WorkStatusSchema } from "./work-items.js";
import { OperationalPolicySchema } from "./business-model.js";
import { DomainError } from "./errors.js";
import { ScheduledSendSchema } from "./scheduled-send.js";
import { ANSWER_MESSAGE_DRAFT_MAX_LENGTH, isAgentProposalMessageDraftWithinBound, isUnicodeScalarTextWithinLength } from "./proposal-message-draft.js";
export { ANSWER_MESSAGE_DRAFT_MAX_LENGTH, STANDARD_AGENT_MESSAGE_DRAFT_MAX_LENGTH, agentProposalMessageDraftMaxLength, isAgentProposalMessageDraftWithinBound, isUnicodeScalarTextWithinLength, unicodeScalarPrefix, unicodeScalarLength } from "./proposal-message-draft.js";
const AgentProposalMessageDraftTransportSchema = z.string().trim().min(1)
    .max(ANSWER_MESSAGE_DRAFT_MAX_LENGTH * 2).refine((value) => isUnicodeScalarTextWithinLength(value, ANSWER_MESSAGE_DRAFT_MAX_LENGTH), { message: "The message draft must contain at most 2,000 valid Unicode characters." });
export const AutomationModeSchema = z.enum(["SHADOW", "REVIEW", "SAFE_AUTO", "FULL_AUTO", "PAUSED"]);
export const AgentProposalTypeSchema = z.enum([
    "BOARD_UPDATE",
    "NEW_WORK",
    "BUSINESS_RULE_UPDATE",
    "ANSWER_MESSAGE",
    "FOLLOWUP_MESSAGE",
    "CLARIFICATION_MESSAGE",
    "ESCALATION_MESSAGE"
]);
export const AgentProposalRiskSchema = z.enum(["LOW", "MEDIUM", "HIGH"]);
export const AgentProposalStateSchema = z.enum([
    "READY_FOR_REVIEW",
    "APPROVED",
    "REJECTED",
    "SNOOZED",
    "EXPIRED",
    "EXECUTING",
    "SUCCEEDED",
    "CANCELLED",
    "FAILED",
    "DELIVERY_UNCERTAIN"
]);
export const AgentProposalCreatorSchema = z.enum(["RULE", "MODEL", "SYSTEM"]);
export const AgentModeSelectionSchema = z.enum(["MANUAL", "AUTO"]);
export const AgentTransportModeSchema = z.enum(["BLOCKED", "APPROVED_ONLY", "POLICY_AUTO"]);
export const AgentControlStateSchema = z.object({
    mode: AutomationModeSchema,
    selection: AgentModeSelectionSchema,
    version: z.number().int().positive(),
    autoAvailable: z.boolean(),
    eligibleActionTypes: z.array(AgentProposalTypeSchema),
    transportMode: AgentTransportModeSchema
}).strict();
export const UpdateAgentControlModeSchema = z.object({
    selection: AgentModeSelectionSchema,
    expectedVersion: z.number().int().positive()
}).strict();
export const AgentBoardPatchSchema = z.object({
    title: z.string().trim().min(1).max(240).optional(),
    description: z.string().trim().max(10_000).optional(),
    clientId: z.uuid().nullable().optional(),
    projectId: z.uuid().nullable().optional(),
    status: WorkStatusSchema.optional(),
    priority: WorkPrioritySchema.optional(),
    assigneeId: z.uuid().nullable().optional(),
    assignedByMemberId: z.uuid().nullable().optional(),
    assignedAt: z.iso.datetime().nullable().optional(),
    department: WorkDepartmentSchema.nullable().optional(),
    filesReference: WorkLocationReferenceSchema.nullable().optional(),
    reviewFeedbackReference: WorkLocationReferenceSchema.nullable().optional(),
    dueAt: z.iso.datetime().nullable().optional(),
    taskStartAt: z.iso.datetime().nullable().optional(),
    taskEndAt: z.iso.datetime().nullable().optional(),
    nextCheckpointAt: z.iso.datetime().nullable().optional(),
    workKind: WorkKindSchema.optional(),
    scheduledStartAt: z.iso.datetime().nullable().optional(),
    scheduledEndAt: z.iso.datetime().nullable().optional(),
    scheduledAllDay: z.boolean().optional(),
    blockedReason: z.string().trim().max(2_000).nullable().optional()
}).strict();
export const BoardUpdateEffectSchema = z.object({
    kind: z.literal("BOARD_UPDATE"),
    workItemId: z.uuid(),
    expectedVersion: z.number().int().positive(),
    changes: AgentBoardPatchSchema,
    note: z.string().trim().min(1).max(10_000).nullable().default(null)
}).strict().superRefine((effect, context) => {
    if (Object.keys(effect.changes).length === 0 && effect.note === null) {
        context.addIssue({ code: "custom", message: "A board proposal needs at least one field or note." });
    }
});
export const NewWorkEffectSchema = z.object({
    kind: z.literal("NEW_WORK"),
    draft: CreateWorkItemSchema
}).strict();
export const BusinessRuleUpdateEffectSchema = z.object({
    kind: z.literal("BUSINESS_RULE_UPDATE"),
    expectedBusinessModelVersion: z.number().int().positive(),
    policy: OperationalPolicySchema
}).strict();
export const OutboundAttachmentKindSchema = z.enum(["IMAGE", "DOCUMENT", "VIDEO"]);
export const OutboundAttachmentSchema = z.object({
    assetId: z.uuid(),
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    kind: OutboundAttachmentKindSchema,
    mimeType: z.string().trim().min(3).max(160).regex(/^[^\s/]+\/[^\s/]+$/),
    displayName: z.string().trim().min(1).max(240),
    byteCount: z.number().int().positive().max(32 * 1024 * 1024)
}).strict();
export const WorkResultDependencySchema = z.object({
    proposalId: z.uuid(),
    workItemId: z.uuid(),
    workItemVersion: z.number().int().positive()
}).strict();
export const AgentEvidenceAttachmentSchema = z.object({
    kind: z.enum(["IMAGE", "DOCUMENT", "AUDIO", "VIDEO"]),
    mimeType: z.string().trim().min(3).max(160).regex(/^[^\s/]+\/[^\s/]+$/),
    displayName: z.string().trim().min(1).max(240),
    byteCount: z.number().int().positive().max(32 * 1024 * 1024)
}).strict();
export const SendMessageEffectSchema = z.object({
    kind: z.literal("SEND_MESSAGE"),
    // WHY [BOL-PENDING-ROUTE-001]: deterministic follow-up timing does not
    // depend on knowing the WhatsApp destination. A draft may therefore enter
    // Manual Review without a route; approval and execution still require a
    // current, versioned conversation policy and can never accept null.
    conversationId: z.uuid().nullable().default(null),
    replyToSourceEventId: z.uuid().nullable().default(null),
    followupInstanceId: z.uuid().nullable().default(null),
    // WHY [BOL-MENTION-CONTRACT-001]: Review binds a workspace member, never a
    // raw channel identifier, into the approved revision. One target keeps a
    // group follow-up focused and prevents a malformed draft from notifying a
    // crowd; the executor resolves the current WhatsApp mapping at send time.
    mentionMemberIds: z.array(z.uuid()).max(1).default([]),
    // WHY [BOL-OUTBOUND-ASSET-CONTRACT-001]: an approval must bind the exact
    // bytes, not a mutable filename. Replacement/removal therefore changes this
    // immutable fingerprint and creates a new proposal revision; old approvals
    // cannot authorize a different file. Omitted preserves legacy text effects.
    // Falsifier: changing bytes or asset identity leaves a receipt executable.
    attachment: OutboundAttachmentSchema.optional(),
    // WHY [BOL-SCHEDULE-REVISION-001]: future authority freezes the Work owner,
    // wall time, timezone rule, offset, UTC instant, and bounded catch-up window
    // inside the same immutable effect as the route. Editing any field creates a
    // new revision and invalidates the old receipt. Falsifier: approved content,
    // destination, Work, or timing changes without a new approval hash.
    schedule: ScheduledSendSchema.optional(),
    // WHY [BOL-SCHEDULE-WORK-DEPENDENCY-001]: one reviewed Task + Message may
    // execute as two durable jobs. The Send must name the exact Work proposal
    // result it depends on; queue order is not authority and multiple workers
    // may claim due jobs concurrently. This tuple is inside the approval hash.
    // Falsifier: transport starts before that proposal succeeds with this exact
    // Work identity/version, or the dependency can change after approval.
    workDependency: WorkResultDependencySchema.optional()
}).strict().superRefine((effect, context) => {
    if (effect.workDependency && !effect.schedule) {
        context.addIssue({ code: "custom", path: ["workDependency"], message: "A Work dependency requires a scheduled message." });
    }
    if (effect.workDependency && effect.schedule
        && effect.workDependency.workItemId !== effect.schedule.workItemId) {
        context.addIssue({ code: "custom", path: ["workDependency", "workItemId"], message: "The Work dependency must match the scheduled Work item." });
    }
});
export const AgentProposalEffectSchema = z.discriminatedUnion("kind", [
    BoardUpdateEffectSchema,
    NewWorkEffectSchema,
    BusinessRuleUpdateEffectSchema,
    SendMessageEffectSchema
]);
export const AgentProposalEvidenceSchema = z.object({
    sourceType: z.enum(["MESSAGE", "BOARD", "FACT", "POLICY"]),
    sourceEventId: z.uuid().nullable().default(null),
    // WHY [BOL-CHANNEL-EVIDENCE-001]: proof belongs to the task workflow, not to
    // WhatsApp. Every channel adapter writes the same immutable source-event ID;
    // this optional display projection lets Review say who/where without making
    // the core understand WhatsApp JIDs, Instagram thread IDs, email message IDs,
    // or future provider links. The internal source event remains the durable
    // locator when a provider has no safe exact-message deep link. Falsifier: an
    // Instagram/Threads adapter must change the proposal/approval schema merely
    // to show trustworthy source context.
    channel: z.string().trim().regex(/^[A-Z][A-Z0-9_]{0,31}$/).nullable().default(null),
    conversationLabel: z.string().trim().min(1).max(200).nullable().default(null),
    senderLabel: z.string().trim().min(1).max(160).nullable().default(null),
    // WHY [BOL-CHANNEL-SOURCE-LINK-001]: exact source navigation is an optional
    // adapter capability, never the evidence itself. WhatsApp private/group
    // messages currently fall back to the stored source card; an Instagram,
    // Threads, or email adapter may supply one HTTPS link only when it can also
    // state honestly whether that link opens the exact source or merely the
    // conversation. The paired kind prevents a chat-level URL from being sold
    // to the reviewer as exact proof. Provider IDs remain behind the adapter.
    // Falsifier: Review invents a WhatsApp permalink, accepts javascript/data
    // URLs, or labels an ordinary conversation link "Open original".
    sourceUrl: z.string().trim().url().refine((value) => new URL(value).protocol === "https:", {
        message: "A source link must use HTTPS."
    }).nullable().optional(),
    sourceUrlKind: z.enum(["EXACT_SOURCE", "CONVERSATION"]).nullable().optional(),
    // WHY [BOL-EVIDENCE-FILE-BRIDGE-001]: the source event proves why a draft
    // exists, while this bounded projection tells Review that the exact retained
    // WhatsApp bytes are available to attach. It contains no storage path, hash,
    // provider ID, or send authority; selecting it still creates a new proposal
    // revision and requires approval. Falsifier: a browser can name a private
    // path/hash, attach unavailable bytes, or forward a file without approval.
    attachment: AgentEvidenceAttachmentSchema.optional(),
    label: z.string().trim().min(1).max(100),
    excerpt: z.string().trim().min(1).max(500),
    observedAt: z.iso.datetime()
}).strict().superRefine((evidence, context) => {
    if (Boolean(evidence.sourceUrl) !== Boolean(evidence.sourceUrlKind)) {
        context.addIssue({
            code: "custom",
            path: evidence.sourceUrl ? ["sourceUrlKind"] : ["sourceUrl"],
            message: "A source link and its navigation kind must be supplied together."
        });
    }
});
export const AgentTargetMembershipSchema = z.enum(["PRESENT", "ABSENT", "UNKNOWN", "NOT_APPLICABLE"]);
export const AgentTargetRouteStateSchema = z.enum([
    "READY",
    "TARGET_MISSING",
    "IDENTITY_SYNCING",
    "IDENTITY_UNRESOLVED",
    "IDENTITY_AMBIGUOUS",
    "IDENTITY_REVIEW",
    "IDENTITY_CONFLICT",
    "ROUTE_MISSING",
    "MEMBERSHIP_UNKNOWN",
    "NOT_IN_GROUP"
]);
export const AgentTargetIdentityRecommendationSchema = z.object({
    observedIdentityId: z.uuid(),
    memberId: z.uuid(),
    sourceEventId: z.uuid(),
    observedDisplayName: z.string().trim().min(1).max(160).nullable(),
    membership: AgentTargetMembershipSchema,
    evidence: AgentProposalEvidenceSchema
}).strict();
export const AgentTargetRouteSchema = z.object({
    channel: z.string().trim().regex(/^[A-Z][A-Z0-9_]{0,31}$/),
    memberId: z.uuid().nullable(),
    state: AgentTargetRouteStateSchema,
    membership: AgentTargetMembershipSchema,
    recommendation: AgentTargetIdentityRecommendationSchema.nullable()
}).strict();
export const AgentProposalExpectedVersionsSchema = z.object({
    workItemVersion: z.number().int().positive().nullable().default(null),
    conversationPolicyVersion: z.number().int().positive().nullable().default(null),
    businessModelVersion: z.number().int().positive().nullable().default(null),
    newestMessageAt: z.iso.datetime().nullable().default(null)
}).strict();
// WHY [BOL-DESTINATION-CONFIRMATION-001]: approving message text is not the
// same act as choosing where it goes. The browser must return the exact
// conversation-policy identity and revision shown beside the draft. The API
// and executor then bind that tuple into the receipt hash, so a renamed,
// replaced, or reconfigured destination fails closed before typing or send.
export const MessageDestinationConfirmationSchema = z.object({
    conversationId: z.uuid(),
    policyId: z.uuid(),
    policyVersion: z.number().int().positive()
}).strict();
// WHY [BOL-APPROVAL-BUNDLE-001]: a manager may judge one business intent
// (create the task and authorize its linked message) as one compact UI action,
// while Work and Send remain separate proposals, receipts, jobs, and audits.
// Stable positions make the whole set lockable and reject partial/reordered
// approval. Falsifier: one click can approve only one half, or a client can
// silently swap another proposal into the reviewed bundle.
export const AgentProposalApprovalBundleSchema = z.object({
    id: z.uuid(),
    position: z.number().int().min(1).max(4),
    size: z.number().int().min(2).max(4)
}).strict().refine((bundle) => bundle.position <= bundle.size, {
    message: "A bundle position must be within its declared size."
});
// Safe exact-reread projection of the durable provider invocation receipt.
// attemptCommittedAt is the pre-call at-most-once marker, never invokedAt.
// invokedAt is the observed sendText/sendMedia entry and remains null when a
// crash erased that observation or the guarded call was never entered. The
// opaque ID is not a route or credential; proposal state remains the authority
// for DELIVERY_UNCERTAIN.
export const AgentProviderInvocationSchema = z.object({
    id: z.uuid(),
    attemptCommittedAt: z.iso.datetime(),
    invokedAt: z.iso.datetime().nullable(),
    attemptCount: z.literal(1),
    providerMessageId: z.string().trim().min(1).max(256).nullable()
}).strict();
const ProposalCoreSchema = z.object({
    type: AgentProposalTypeSchema,
    risk: AgentProposalRiskSchema,
    creator: AgentProposalCreatorSchema,
    sourceEventId: z.uuid().nullable().default(null),
    conversationId: z.uuid().nullable().default(null),
    workItemId: z.uuid().nullable().default(null),
    contextManifestId: z.uuid().nullable().default(null),
    modelRunId: z.uuid().nullable().default(null),
    supersedesProposalId: z.uuid().nullable().default(null),
    approvalBundle: AgentProposalApprovalBundleSchema.nullable().default(null),
    title: z.string().trim().min(1).max(240),
    whyNow: z.string().trim().min(1).max(500),
    effect: AgentProposalEffectSchema,
    messageDraft: AgentProposalMessageDraftTransportSchema.nullable().default(null),
    evidence: z.array(AgentProposalEvidenceSchema).max(12).default([]),
    expectedVersions: AgentProposalExpectedVersionsSchema.default({
        workItemVersion: null,
        conversationPolicyVersion: null,
        businessModelVersion: null,
        newestMessageAt: null
    }),
    expiresAt: z.iso.datetime()
}).strict();
export const CreateAgentProposalSchema = ProposalCoreSchema.extend({
    idempotencyKey: z.string().trim().min(10).max(240)
}).strict().superRefine(validateProposalContract);
export const AgentProposalSchema = ProposalCoreSchema.extend({
    id: z.uuid(),
    state: AgentProposalStateSchema,
    destinationLabel: z.string().min(1).max(200).nullable(),
    destinationPolicyId: z.uuid().nullable().default(null),
    destinationPolicyVersion: z.number().int().positive().nullable().default(null),
    // Missing projection data is never interpreted as destination authority.
    destinationPolicyReady: z.boolean().default(false),
    // WHY [BOL-TARGET-READ-MODEL-001]: a valid conversation policy proves only
    // the group's allowed purpose. Review also needs an exact mapped person and
    // current directory membership before it may call the draft send-ready. This
    // hydrated projection carries only opaque IDs and safe source proof; provider
    // identities remain private and are resolved again from live metadata at send.
    // Falsifier: "Verified route" appears for a person absent from that group, or
    // the browser must receive a JID to decide whether Send is enabled.
    targetRoute: AgentTargetRouteSchema.optional(),
    workItemTitle: z.string().min(1).max(240).nullable(),
    providerInvocation: AgentProviderInvocationSchema.optional(),
    currentRevision: z.number().int().positive(),
    approvedRevision: z.number().int().positive().nullable(),
    snoozedUntil: z.iso.datetime().nullable(),
    version: z.number().int().positive(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime()
}).strict().superRefine(validateProposalContract);
export const EditAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive(),
    effect: AgentProposalEffectSchema.optional(),
    // The proposal type is server-owned and loaded under the workspace lock.
    // This transport bound admits the largest type; assertProposalEffectMatchesType
    // applies the exact ANSWER vs compact-message bound before any revision write.
    messageDraft: AgentProposalMessageDraftTransportSchema.nullable().optional()
}).strict().refine((input) => input.effect !== undefined || input.messageDraft !== undefined, {
    message: "Change the message or proposed board fields before saving."
});
export const RouteAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive(),
    conversationId: z.uuid(),
    expectedPolicyVersion: z.number().int().positive()
}).strict();
export const ApproveAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive(),
    revision: z.number().int().positive(),
    destinationConfirmation: MessageDestinationConfirmationSchema.nullable().default(null)
}).strict();
export const ApproveAgentProposalBundleSchema = z.object({
    approvals: z.array(ApproveAgentProposalSchema.extend({
        proposalId: z.uuid()
    }).strict()).min(2).max(4)
}).strict().superRefine((input, context) => {
    const ids = new Set(input.approvals.map((approval) => approval.proposalId));
    if (ids.size !== input.approvals.length) {
        context.addIssue({
            code: "custom",
            path: ["approvals"],
            message: "Each proposal in a bundle may be approved only once."
        });
    }
});
export const ProposalDecisionReasonSchema = z.enum([
    "NOT_NEEDED",
    "WRONG_FACTS",
    "WRONG_RECIPIENT",
    "WRONG_TIMING",
    "WRONG_TONE",
    "DUPLICATE",
    "OTHER"
]);
export const RejectAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive(),
    reason: ProposalDecisionReasonSchema,
    note: z.string().trim().max(500).default("")
}).strict();
export const RestoreRejectedAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive()
}).strict();
export const SnoozeAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive(),
    until: z.iso.datetime()
}).strict();
export const CancelScheduledAgentProposalSchema = z.object({
    expectedVersion: z.number().int().positive(),
    revision: z.number().int().positive()
}).strict();
export function assertProposalEffectMatchesType(type, effect, messageDraft) {
    const parsed = AgentProposalEffectSchema.parse(effect);
    const expectsMessage = [
        "ANSWER_MESSAGE",
        "FOLLOWUP_MESSAGE",
        "CLARIFICATION_MESSAGE",
        "ESCALATION_MESSAGE"
    ].includes(type);
    if (type === "BOARD_UPDATE" && parsed.kind !== "BOARD_UPDATE") {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "That proposal does not contain a board update.");
    }
    if (type === "NEW_WORK" && parsed.kind !== "NEW_WORK") {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "That proposal does not contain new work.");
    }
    if (type === "BUSINESS_RULE_UPDATE" && parsed.kind !== "BUSINESS_RULE_UPDATE") {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "That proposal does not contain a business-rule update.");
    }
    if (expectsMessage && (parsed.kind !== "SEND_MESSAGE" || !messageDraft)) {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "That proposal needs one message draft.");
    }
    if (messageDraft !== null && !isAgentProposalMessageDraftWithinBound(type, messageDraft)) {
        throw new DomainError("INVALID_PROPOSAL_MESSAGE_DRAFT", type === "ANSWER_MESSAGE"
            ? "That answer exceeds the durable message bound."
            : "That operational message must stay within 300 characters.");
    }
    if (parsed.kind === "SEND_MESSAGE"
        && parsed.conversationId === null
        && !["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE"].includes(type)) {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "Only a follow-up draft may wait for destination selection.");
    }
    if (parsed.kind === "SEND_MESSAGE"
        && !["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE"].includes(type)
        && parsed.mentionMemberIds.length > 0) {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "Only a targeted follow-up or escalation may notify a group member.");
    }
    const ownsFollowupLifecycle = ["FOLLOWUP_MESSAGE", "ESCALATION_MESSAGE"].includes(type);
    if (parsed.kind === "SEND_MESSAGE"
        && ownsFollowupLifecycle
        && parsed.followupInstanceId === null) {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "A follow-up or escalation requires one exact lifecycle instance.");
    }
    if (parsed.kind === "SEND_MESSAGE"
        && !ownsFollowupLifecycle
        && parsed.followupInstanceId !== null) {
        // WHY [BOL-GENERIC-MESSAGE-NOT-FOLLOWUP-001]: reply/attempt handling is
        // keyed only by followupInstanceId. Letting a one-off answer or scheduled
        // message carry that ID would silently opt it into Work advancement and
        // no-response escalation. Proposal type and lifecycle ownership must agree.
        // Falsifier: CLARIFICATION/ANSWER can update a follow-up instance, or a
        // FOLLOWUP/ESCALATION reaches persistence without the exact instance.
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "A one-off message cannot carry a follow-up lifecycle instance.");
    }
    if (!expectsMessage && messageDraft !== null) {
        throw new DomainError("INVALID_PROPOSAL_EFFECT", "Non-message proposals cannot hide a separate message action.");
    }
    return parsed;
}
function validateProposalContract(proposal, context) {
    try {
        assertProposalEffectMatchesType(proposal.type, proposal.effect, proposal.messageDraft);
    }
    catch (error) {
        context.addIssue({ code: "custom", message: error instanceof Error ? error.message : "Invalid proposal effect." });
    }
    if (proposal.effect.kind === "SEND_MESSAGE" && proposal.conversationId !== proposal.effect.conversationId) {
        context.addIssue({ code: "custom", message: "The proposal destination does not match its message effect." });
    }
    if (proposal.effect.kind === "BOARD_UPDATE" && proposal.workItemId !== proposal.effect.workItemId) {
        context.addIssue({ code: "custom", message: "The proposal work item does not match its board effect." });
    }
    if (proposal.effect.kind === "SEND_MESSAGE" && proposal.effect.schedule) {
        // WHY [BOL-NEW-WORK-SCHEDULED-FK-001]: a bundled Message may bind the
        // immutable result of NEW_WORK before that Work row exists. Keep the
        // relational FK null only for the exact second member/result-v1 shape;
        // the approval-hashed schedule/dependency still freezes the future ID,
        // and the executor must backfill the FK before the prerequisite succeeds.
        // Falsifier: a standalone/existing-Work send accepts null, or transport
        // can run before the exact created Work row is linked.
        const projectsNewWork = proposal.workItemId === null
            && proposal.approvalBundle?.position === 2
            && proposal.approvalBundle.size === 2
            && proposal.effect.workDependency !== undefined
            && proposal.effect.workDependency.proposalId === proposal.effect.workDependency.workItemId
            && proposal.effect.workDependency.workItemVersion === 1
            && proposal.effect.workDependency.workItemId === proposal.effect.schedule.workItemId;
        if (proposal.conversationId === null) {
            context.addIssue({ code: "custom", message: "A scheduled message requires one exact destination." });
        }
        if (!projectsNewWork && proposal.workItemId !== proposal.effect.schedule.workItemId) {
            context.addIssue({ code: "custom", message: "The scheduled message does not match its Work item." });
        }
        if (proposal.expectedVersions.workItemVersion === null) {
            context.addIssue({ code: "custom", message: "A scheduled message requires the current Work version." });
        }
        if (proposal.effect.workDependency
            && proposal.expectedVersions.workItemVersion !== proposal.effect.workDependency.workItemVersion) {
            context.addIssue({ code: "custom", message: "The scheduled message must expect the exact dependent Work version." });
        }
    }
}
//# sourceMappingURL=agent-proposals.js.map